import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/browser'
import {
  CheckCircle2,
  Fingerprint,
  KeyRound,
  Loader2,
  Lock,
} from 'lucide-react'
import { apiFetch, apiJson } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/useAuth'
import type { Credential } from '@/lib/auth/types'
import { CredentialRow } from '@/components/security/CredentialRow'
import { StepUpTest } from '@/components/security/StepUpTest'
import { ActiveSessions } from '@/components/security/ActiveSessions'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Eyebrow } from '@/components/hawk-primitives'

export const Route = createFileRoute('/_app/settings/security')({
  component: SecuritySettingsPage,
})

function SecuritySettingsPage() {
  const { webauthnEnabled } = useAuth()
  const queryClient = useQueryClient()
  const [showNamePrompt, setShowNamePrompt] = useState(false)
  const [keyName, setKeyName] = useState('')

  const credentials = useQuery({
    queryKey: ['webauthn-credentials'],
    queryFn: () => apiJson<Credential[]>('/auth/webauthn/credentials'),
    enabled: webauthnEnabled,
  })

  const registration = useMutation({
    mutationFn: async (name: string) => {
      const { publicKey } = await apiJson<{
        publicKey: PublicKeyCredentialCreationOptionsJSON
      }>('/auth/webauthn/register/begin', {
        method: 'POST',
        body: JSON.stringify({ name: name || null }),
      })
      const credential = await startRegistration({ optionsJSON: publicKey })
      const response = await apiFetch('/auth/webauthn/register/complete', {
        method: 'POST',
        body: JSON.stringify(credential),
      })
      if (!response.ok) {
        throw new Error((await response.text()) || 'Registration failed')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webauthn-credentials'] })
      setShowNamePrompt(false)
      setKeyName('')
    },
    onError: () => {
      setShowNamePrompt(false)
      setKeyName('')
    },
  })

  function handleConfirmRegister() {
    registration.mutate(keyName.trim())
  }

  const hasCredentials =
    webauthnEnabled && (credentials.data?.length ?? 0) > 0

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Security"
        description="Hardware keys, passkeys, and active session control. Protects every privileged action with step-up authentication."
      />

      <section className="animate-hawk-rise" style={{ animationDelay: '0ms' }}>
        <div className="mb-4 flex items-baseline justify-between flex-wrap gap-2">
          <div className="flex items-baseline gap-3">
            <Eyebrow>Security keys</Eyebrow>
            {webauthnEnabled && credentials.data && (
              <span className="font-mono text-xs text-muted-foreground tabular-nums">
                {credentials.data.length} registered
              </span>
            )}
          </div>
        </div>

        <div className="relative border border-border bg-card rounded-md overflow-hidden">
          <div
            className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent pointer-events-none"
            aria-hidden
          />
          <div className="p-6">
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl mb-5">
              {webauthnEnabled
                ? 'Register a hardware key or biometric authenticator. Required for step-up authentication on privileged actions like deleting credentials or revoking sessions.'
                : 'Security key authentication is disabled by the administrator. Contact your administrator to enable WebAuthn.'}
            </p>

            {webauthnEnabled && (
              <>
                {registration.status === 'success' && (
                  <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 border border-primary/30 text-primary">
                    <CheckCircle2 className="size-4" strokeWidth={1.5} />
                    <span className="text-sm">Security key registered.</span>
                  </div>
                )}
                {registration.error && (
                  <div className="mb-4 text-sm text-destructive">
                    {registration.error.message}
                  </div>
                )}

                {credentials.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    Loading credentials...
                  </div>
                ) : credentials.data && credentials.data.length > 0 ? (
                  <div className="space-y-2">
                    {credentials.data.map((cred) => (
                      <CredentialRow
                        key={cred.id}
                        credential={cred}
                        onUpdated={() =>
                          queryClient.invalidateQueries({
                            queryKey: ['webauthn-credentials'],
                          })
                        }
                      />
                    ))}
                  </div>
                ) : credentials.data?.length === 0 ? (
                  <EmptyKeys />
                ) : null}

                {showNamePrompt && registration.status !== 'pending' ? (
                  <div className="mt-5 space-y-3 rounded-md bg-secondary/60 border border-border p-4">
                    <div>
                      <label
                        htmlFor="key-name"
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground"
                      >
                        Name your security key
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Recognizable name like "YubiKey 5" or "MacBook Touch ID".
                      </p>
                    </div>
                    <Input
                      id="key-name"
                      value={keyName}
                      onChange={(e) => setKeyName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleConfirmRegister()
                        if (e.key === 'Escape') setShowNamePrompt(false)
                      }}
                      autoFocus
                      maxLength={64}
                      placeholder="e.g. YubiKey 5 NFC"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleConfirmRegister}>
                        <Lock className="size-4" />
                        Continue
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setShowNamePrompt(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5">
                    <Button
                      onClick={() => {
                        setShowNamePrompt(true)
                        setKeyName('')
                      }}
                      disabled={registration.status === 'pending'}
                    >
                      {registration.status === 'pending' ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          Waiting for security key...
                        </>
                      ) : (
                        <>
                          <KeyRound className="size-4" />
                          Register security key
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {hasCredentials && (
        <section
          className="mt-10 animate-hawk-rise"
          style={{ animationDelay: '80ms' }}
        >
          <StepUpTest credentials={credentials.data!} />
        </section>
      )}

      <section
        className="mt-10 animate-hawk-rise"
        style={{ animationDelay: '160ms' }}
      >
        <ActiveSessions />
      </section>
    </div>
  )
}

function EmptyKeys() {
  return (
    <div className="border border-border border-dashed rounded-md py-10 px-6 text-center bg-card/50">
      <div className="inline-flex items-center justify-center size-12 rounded-md bg-secondary border border-border mb-4">
        <Fingerprint
          className="size-5 text-muted-foreground"
          strokeWidth={1.5}
        />
      </div>
      <div className="text-sm font-medium mb-1">No security keys registered</div>
      <p className="text-xs text-muted-foreground">
        Register one below to enable step-up authentication.
      </p>
    </div>
  )
}
